<!--
  Auto Followups tab — SMS mirror of EmailInvites23/EmailFollowup.vue (June '26).
  Saves to entities.textSettings via UPDATE_TEXT_SETTINGS; the processTextFollowups
  cron sends "BusinessName - <followup text> <original invite link>".
-->
<template>
    <div class="pb-64">
        <p class="my-4 text-lg">Enable automatic text reminders to your customers when they don't respond to text invites. These settings will also be applied to Dev API text invites.</p>
        <div class="p-4 my-3 text-lg font-medium rounded flex items-center lightbrandbg">
            <feather-icon icon="InfoIcon" svgClasses="stroke-current text-light w-6 h-6" class="mx-2" />
            <span>
                <p>Followup texts use credits the same way invites do{{ byoTextActive ? ' (not while you\'re sending via your own Twilio account)' : '' }}. Please be respectful of your recipients' privacy — on the final text, be sure to mention that they won't be bothered again.</p>
            </span>
        </div>


        <div class="vx-card p-5 py-6">
            <p class="mb-6 flex max-w-lg font-semibold" >Send an initial followup text
            <vs-switch class="ml-2" v-model="followupOnce">
              <span slot="on">On</span>
              <span slot="off">Off</span>
            </vs-switch></p>
            <vs-radio vs-name="onceperiod" :disabled="!followupOnce" v-model="followupPeriodOnce" vs-value="2d" class="mr-3 text-dark font-medium">
                <p class="text-center">2 days after invite</p>
            </vs-radio>
            <vs-radio vs-name="onceperiod"  :disabled="!followupOnce" v-model="followupPeriodOnce" vs-value="3d" class="mr-3  text-dark font-medium">
                <p class="text-center">3 days after invite</p>
            </vs-radio>
            <vs-radio vs-name="onceperiod"  :disabled="!followupOnce" v-model="followupPeriodOnce" vs-value="7d" class="mr-3 text-dark font-medium">
                <p class="text-center">7 days after invite</p>
            </vs-radio>
            <p class="mt-6 mb-4 max-w-lg" >How would you like to followup initially?</p>
            <vs-textarea :disabled="!followupOnce" name="Followup text" v-validate="'max:200|required'" size="large" class="w-full" placeholder="Just a friendly reminder — we'd still love your quick feedback:" v-model="followupTextOnce"/>
            <span class="text-danger block mt-2">{{ errors.first('Followup text') }}</span>
            <p class="text-sm mt-2">{{ charHint(followupTextOnce) }}</p>
            <!-- SMS preview: exactly how processTextFollowups assembles the message -->
            <div class="p-4 my-3 rounded" style="background:#f0f4ff;" v-if="followupOnce">
                <p class="font-medium mb-1">Text preview</p>
                <p class="text-dark">{{ smsPreview(followupTextOnce) }}</p>
            </div>
        </div>


        <div class="vx-card mt-4 p-5 py-6" v-if="followupOnce">
            <p class="mb-4 flex max-w-lg font-semibold" >Send a final followup text
            <vs-switch class="ml-2" v-model="followupTwice">
              <span slot="on">On</span>
              <span slot="off">Off</span>
            </vs-switch></p>
            <vs-radio vs-name="twiceperiod"   :disabled="!followupTwice" v-model="followupPeriodTwice" vs-value="2d" class="mr-3 text-dark font-medium">
                <p class="text-center">2 days after initial followup</p>
            </vs-radio>
            <vs-radio vs-name="twiceperiod"  :disabled="!followupTwice" v-model="followupPeriodTwice" vs-value="3d" class="mr-3 text-dark font-medium">
                <p class="text-center">3 days after initial followup</p>
            </vs-radio>
            <vs-radio vs-name="twiceperiod"  :disabled="!followupTwice"  v-model="followupPeriodTwice" vs-value="7d" class="mr-3 text-dark font-medium">
                <p class="text-center">7 day after initial followup</p>
            </vs-radio>
            <p class="mt-6 mb-4 max-w-lg" >How would you like to followup on the final text?</p>
            <vs-textarea :disabled="!followupTwice" name="Final followup text" v-validate="'max:200|required'" size="large" class="w-full" placeholder="One last nudge — we'd really value your feedback. We won't text you about this again:" v-model="followupTextTwice"/>
            <span class="text-danger block mt-2">{{ errors.first('Final followup text') }}</span>
            <p class="text-sm mt-2">{{ charHint(followupTextTwice) }}</p>
            <div class="p-4 my-3 rounded" style="background:#f0f4ff;" v-if="followupTwice">
                <p class="font-medium mb-1">Text preview</p>
                <p class="text-dark">{{ smsPreview(followupTextTwice) }}</p>
            </div>
        </div>

        <savebutton submitText="Update" :accept="update" :goodToGo="formReady" />
    </div>
</template>

<script>

    import Savebutton from '../components/ptcomponents/Savebutton.vue'

export default {
    data() {
        return {
            startingData: '',
            followupTextOnce: 'Just a friendly reminder — we\'d still love your quick feedback:',
            followupTextTwice: 'One last nudge — we\'d really value your feedback. We won\'t text you about this again:',
            followupPeriodOnce: '2d',
            followupPeriodTwice: '2d',
            followupOnce: false,
            followupTwice: false
        }
    },
    computed: {
        byoTextActive() {
            return this.$store.getters['dash/byoTextActive']
        },
        formReady() {
            if (this.startingData === this.getNowStateJSON()) {
                return false
            } else {
                if (this.errors.any()) return false
            }
            return true
        }
    },
    components: {
        Savebutton
    },
    methods: {
        smsPreview(followupText) {
            //mirrors processTextFollowups: businessName + ' - ' + followupText + ' ' + inviteLink
            const businessName = this.$store.state.dash.viewData.business.businessName
            const tokenJoin = this.$store.getters['dash/hasCustomDomainString'] ? '/' : '-'
            const link = `${this.$store.getters['dash/feedbackURL']}${tokenJoin}TOKEN`
            return `${businessName} - ${followupText} ${link}`
        },
        charHint(followupText) {
            const total = this.smsPreview(followupText).length
            //standard GSM segment is 160 chars; longer texts send (and cost) multiple segments
            return total <= 160
              ? `About ${total} characters including your business name and the link — fits in one text.`
              : `About ${total} characters including your business name and the link — texts over 160 characters are sent (and billed) as multiple segments. Consider shortening it.`
        },
        getNowStateJSON() {
            return JSON.stringify({
                followupTextOnce: this.followupTextOnce,
                followupTextTwice: this.followupTextTwice,
                followupPeriodOnce: this.followupPeriodOnce,
                followupPeriodTwice: this.followupPeriodTwice,
                followupOnce: this.followupOnce,
                followupTwice: this.followupTwice
            })
        },
        update() {
            this.$store.dispatch('dash/UPDATE_TEXT_SETTINGS', {
                followupTextOnce: this.followupTextOnce,
                followupTextTwice: this.followupTextTwice,
                followupPeriodOnce: this.followupPeriodOnce,
                followupPeriodTwice: this.followupPeriodTwice,
                followupOnce: this.followupOnce,
                followupTwice: this.followupTwice
            }).then(() => {
        this.$vs.notify({
          title: 'Update Text Followup Settings',
          text: 'Followup settings updated successfully',
          color: 'success',
          time: 5000,
          iconPack: 'feather',
          icon:'icon-check'})
          this.startingData = this.getNowStateJSON()
      }).catch(() => {
        this.$vs.notify({
          title: 'Update Text Followup Settings',
          text:'Oops! Something went wrong, and we couldn\'t update your text settings. Please try again, and reach out for support if it happens again.',
          color: 'warning',
          time: 10000,
          iconPack: 'feather',
          icon:'icon-alert-circle'})
      })
        },
        init() {
            //does exist on entity
            const preState = this.$store.getters['dash/textSettings']
            if (preState) {
                this.followupTextOnce = preState.followupTextOnce,
                this.followupTextTwice =  preState.followupTextTwice,
                this.followupPeriodOnce = preState.followupPeriodOnce,
                this.followupPeriodTwice = preState.followupPeriodTwice,
                this.followupOnce = preState.followupOnce,
                this.followupTwice = preState.followupTwice
            }
        this.startingData = this.getNowStateJSON()
        }
    },
    created() {

        this.init()

    }
}

</script>
